import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  Icon,
  linkTextClasses,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  rowLinkClasses,
  Table,
} from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';

/**
 * Os planos — `/erp/planos`.
 *
 * O plano é o GRUPO DE PRODUTO do ERP ("PLANO PRATA - PARTICULAR"), decidido em
 * 19/10/2026 contra o que o código já registrava (`db/schema/external.ts`): não
 * é o produto do veículo, que é a linha do contrato e tem a tela de Produtos.
 *
 * A lista cruza o que o ERP lista com o que os carros apontam, e mostra as duas
 * sobras em vez de escondê-las: o plano sem carro (zero) e o código que algum
 * carro aponta e a lista não tem ("fora da lista"). Abrir um plano leva aos
 * veículos dele.
 */
export function PlansScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/planos' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: ['erp', 'planos', tenantId, term, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchPlans({
        body: { term: term.length >= 1 ? term : undefined, page, perPage },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os planos'));
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });
  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);

  return (
    <Page measure="cheia">
      <PageHeader title="Planos">
        Os planos de {context?.currentTenant.nome} — os grupos de produto do ERP — e
        quantos carros têm cada um.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {/* só explica o plano sem nome; sem plano nenhum, a tabela diz que não há dados */}
      {result && !result.listSynced && result.items.length > 0 && (
        <Alert tone="alerta">
          A lista de planos do ERP ainda não chegou a esta base — ela vem na passagem das
          listas de referência, uma vez por noite. Até lá os planos aparecem só pelo
          código que os carros apontam, sem nome.{' '}
          <Link to="/erp/saude" className={linkTextClasses()}>
            Ver a saúde da integração
          </Link>
        </Alert>
      )}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} plano(s)`}
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar planos',
              placeholder: 'Nome ou código',
              value: text,
              onChange: setText,
            }}
            label="Planos"
            items={result?.items ?? []}
            loading={query.isLoading}
            rowKey={(p) => p.code}
            empty={term ? 'Nenhum plano com esse termo.' : 'Não há planos nesta base.'}
            rowLink={(p) =>
              p.vehicles > 0 ? (
                <Link
                  to="/erp/veiculos"
                  search={{ plano: p.code }}
                  aria-label={`Ver os veículos do ${p.label ?? `plano ${p.code}`}`}
                  className={rowLinkClasses}
                >
                  <Icon nome="seta-direita" size={16} />
                </Link>
              ) : null
            }
            columns={[
              {
                title: 'Plano',
                minWidth: '18rem',
                cell: (p) => (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {p.label ?? <NotProvided>sem nome</NotProvided>}
                    {/* sem lista não há "fora da lista": o aviso lá em cima diz o porquê */}
                    {!p.inList && result?.listSynced && (
                      <Badge tone="alerta">fora da lista do ERP</Badge>
                    )}
                  </span>
                ),
              },
              { title: 'Código no ERP', minWidth: '8rem', cell: (p) => p.code },
              {
                title: 'Veículos',
                alignment: 'fim',
                cell: (p) => <span className="tabular-nums">{p.vehicles}</span>,
              },
            ]}
          />
          {result && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="planos"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
